<script lang="ts" setup>
import { ref, watch, computed, onMounted, onUnmounted, nextTick, onUpdated } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { useSelectInputStore } from './useSelectInputStore';
import { storeToRefs } from 'pinia';
import {
  AggregateBusinessFilterProperty,
  FilterType,
  ForecastTimelineFilterProperty,
  isFetchRequiredFilterType,
} from '@/coreDomainsModule/entities/filter';
import { FilterInfoState } from './interface/uiState';
import { IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterInfoRepository } from '@/coreInfrastructureModule/Repositories/FilterInfoRepository';
import { i18n } from '@/i18n';
import { SelectInputViewModel } from './selectInputViewModel';
import ErrorMessage from '@/corePagesModule/components/ErrorMessage.vue';
import { FilterModalType } from '../filterModal/filterModal';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';

// FIXME: SelectInput周りは絶対に全部壊して作り直す

const props = withDefaults(
  defineProps<{
    index: number;
    selectedRepresent: string;
    placeholder: string;
    label: string;
    disabled?: boolean;
    filterType: FilterType;
    isOpenProp?: boolean;
    isError?: boolean;
    errorMessage?: string;
    isContinueOpening?: boolean;
    propAggregateBusinessFilterProperty?: AggregateBusinessFilterProperty;
    propCustomerForecastFilterProperty?: ForecastTimelineFilterProperty;
    disableUnassignedColumn?: boolean; // nullableな項目でもUnassignedColumnを出さない様にする
    // 以下は使用元で選択肢を指定してfetch処理を括り出す場合に使用
    propOptions?: string[];
    propPagingState?: FilterInfoState;
    propPagingOptions?: IPagingOptions;
    propOriginalOptions?: string[];
    useTeleport?: boolean;
    filterModalType?: FilterModalType;
    resetButtonText?: string;
    useSearchInput?: boolean;
    useReset?: boolean;
    resetMode?: 'empty-option' | 'reset';
    errorMessagePosition?: 'top' | 'bottom';
  }>(),
  {
    errorMessage: '',
    propAggregateBusinessFilterProperty: undefined,
    propCustomerForecastFilterProperty: undefined,
    /**
     * propOption初期化 defaultでArray, Objectを指定する場合ファクトリー関数を用いる必要がある
     */
    propOptions: () => [] as string[],
    propPagingState: undefined,
    propPagingOptions: undefined,
    /**
     * propOptionsから正規化に利用する元配列を受け取る。未指定時は空配列にする。
     */
    propOriginalOptions: () => [] as string[],
    isOpenProp: false,
    filterModalType: 'deletion-business',
    resetButtonText: undefined,
    useSearchInput: true,
    useReset: true,
    resetMode: 'empty-option',
    errorMessagePosition: 'top',
  }
);

const isOpen = ref(false);
const t = i18n.t;
const store = useSelectInputStore(props.filterType, props.filterModalType, props.index)();
const { state } = storeToRefs(store);
const { $reset, setFilterInfo, fetchFilterInfo, searchItem } = store;
const MIN_SELECT_BOX_HEIGHT = 240;
const isEllipsis = ref(false);

const filterInfoRepository = new FilterInfoRepository();
const vm = new SelectInputViewModel();

const emit = defineEmits<{
  (e: 'click:selectInput'): void;
  (e: 'update', value: string, index?: number): void;
  (e: 'click:seeMore'): void;
  (e: 'close'): void;
  (e: 'selectedListResized', bottom: number): void;
  (e: 'reset'): void;
  (e: 'search', keyword: string);
}>();

const dropdownSelectedListDom = ref<HTMLDivElement | null>(null);

watch(
  () => props.isOpenProp,
  () => {
    isOpen.value = props.isOpenProp;
  }
);

watch(
  () => props.propOptions,
  () => {
    if (props.propOptions) {
      setFilterInfo(props.propOptions);
    }
  }
);

watch(
  () => props.propPagingState,
  () => {
    if (props.propPagingState) {
      state.value.pagingState = props.propPagingState;
    }
  }
);

watch(
  () => props.propPagingOptions,
  () => {
    if (props.propPagingOptions) {
      state.value.pagingOptions = props.propPagingOptions;
    }
  }
);

onMounted(() => {
  $reset();
});

/**
 * placeholderを表示するかどうか
 */
const isPlaceholder = computed(() => {
  return props.selectedRepresent === '' || props.selectedRepresent === null || props.selectedRepresent === undefined;
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
    if (!isOpen.value) {
      $reset();
    }
  }
};

/**
 * propsからオリジナルの選択肢リストを取得する。
 * @returns 正規化対象のオプション配列
 */
const getOriginalOptions = (): string[] => {
  if (props.propOriginalOptions && props.propOriginalOptions.length > 0) {
    return props.propOriginalOptions;
  }
  return props.propOptions ?? [];
};

/**
 * 選択肢を正規化して親にemitする。
 * @param option - 選択されたオプション
 * @param index - 選択肢が見つからなかった場合に使うフォールバックのインデックス
 */
const emitOption = (option: string, index?: number) => {
  const originalOptions = getOriginalOptions();
  const normalizedIndex = originalOptions.length > 0 ? originalOptions.indexOf(option) : index !== undefined ? index : undefined;
  emit('update', option, normalizedIndex !== undefined && normalizedIndex >= 0 ? normalizedIndex : index);
  if (!props.isContinueOpening) isOpen.value = false;
};

/**
 * 選択値をリセットします。
 */
const resetOption = () => {
  emit('reset');
  if (!props.isContinueOpening) isOpen.value = false;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = `#order-status-select-${props.index}`;
  const arrowSelector = `#order-status-select-arrow-${props.index}`;
  const searchSelector = `#order-status-select-search-${props.index}`;
  const dropDownSelector = `#order-status-select-dropdown-${props.index}`;
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.tagName === 'path' || target.tagName === 'svg') return; // 複数選択でのリスト内のxボタンを弾く
  if (target.closest(selector) && target.className.indexOf('select-box__confirm--button') < 0) return;
  if (target.closest(searchSelector)) return;
  if (target.closest(dropDownSelector)) return;

  isOpen.value = false;
  emit('close');
};

/**
 * 追加読み込み
 */
const onClickAppendButton = () => {
  fetchFilterInfo(props.propAggregateBusinessFilterProperty, props.propCustomerForecastFilterProperty);
  emit('click:seeMore');
};

/**
 * 再試行
 */
const onClickRetryButton = () => {
  fetchFilterInfo(props.propAggregateBusinessFilterProperty, props.propCustomerForecastFilterProperty);
  emit('click:seeMore');
};

/**
 * 検索トリガー
 * @param event - イベント
 */
const onSearchTrigger = (event?: Event) => {
  // ローディング中の場合と、日本語IMEの変換中は実行しない
  if (state.value.pagingState.type === 'loading' || (event instanceof KeyboardEvent && event.isComposing)) {
    return;
  }
  searchItem(props.propAggregateBusinessFilterProperty, props.propCustomerForecastFilterProperty, props.propOptions);
  emit('search', state.value.searchText);
};

const resizeObserver = new ResizeObserver((entries) => {
  emit('selectedListResized', entries[0].target.clientHeight);
});

onMounted(() => {
  document.addEventListener('click', closeDropdown);
  window.addEventListener('resize', calculateDropdownPosition);
  window.addEventListener('scroll', calculateDropdownPosition, true);
});

onUpdated(() => {
  if (dropdownSelectedListDom.value) resizeObserver.observe(dropdownSelectedListDom.value);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
  window.removeEventListener('resize', calculateDropdownPosition);
  window.removeEventListener('scroll', calculateDropdownPosition, true);
  if (dropdownSelectedListDom.value) resizeObserver.unobserve(dropdownSelectedListDom.value);
});

const OPTION_HEIGHT = 32; // Height of one option item in pixels

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    const selectBox = document.querySelector(`#order-status-select-${props.index}`) as HTMLElement;
    if (selectBox) {
      const rect = selectBox.getBoundingClientRect();
      const dropdown = document.querySelector(`#order-status-select-dropdown-${props.index}`) as HTMLElement;
      if (dropdown) {
        dropdown.style.left = `${rect.left - 1}px`; // スクロールを考慮した絶対位置 MultipleSelectInputで使用した際に選択リストの背景色が見えてしまうので左に1pxずらしています
        dropdown.style.top = `${rect.bottom + 1}px`; // スクロールを考慮した絶対位置 若干セレクトボックスにかぶってしまうので1px下にずらしています
        dropdown.style.width = `${MIN_SELECT_BOX_HEIGHT > rect.width ? MIN_SELECT_BOX_HEIGHT : rect.width}px`; // select-boxの幅に合わせて設定

        // Calculate height based on 16 option items
        const desiredHeight = OPTION_HEIGHT * 16;

        // max-heightを計算して設定
        const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
        dropdown.style.maxHeight = `${Math.min(desiredHeight, availableHeight)}px`; // Set max height
        dropdown.style.overflowY = 'auto'; // スクロール可能にする
      }
    }
  });
};

/**
 * 読み込み中のテキストを取得するcomputed
 */
const loadingText = ref('読み込み中.');

let loadingInterval: number | undefined;

watch(isOpen, async (newVal) => {
  if (newVal) {
    emit('click:selectInput');
    calculateDropdownPosition();
    if (props.propOptions) {
      setFilterInfo(props.propOptions);
    }
    startLoadingAnimation();
    if (isFetchRequiredFilterType(props.filterType)) {
      await fetchFilterInfo(props.propAggregateBusinessFilterProperty, props.propCustomerForecastFilterProperty);
    }
  } else {
    $reset();
    stopLoadingAnimation();
  }
});

/**
 * ローディングアニメーションを開始します。
 */
const startLoadingAnimation = () => {
  loadingText.value = `${t('読み込み中')}.`; // Initialize with one dot
  loadingInterval = window.setInterval(() => {
    const dots = loadingText.value.match(/\./g)?.length || 0;
    loadingText.value = `${t('読み込み中')}${'.'.repeat((dots + 1) % 4)}`;
  }, 500);
};

/**
 * ローディングアニメーションを停止します。
 */
const stopLoadingAnimation = () => {
  if (loadingInterval) {
    clearInterval(loadingInterval);
    loadingInterval = undefined;
  }
};

/**
 * テキストにマウスオーバーしたときに、テキストが省略されているかどうかを判定する
 * @param e - イベント
 */
const mouseoverTextLabel = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  isEllipsis.value = target.offsetWidth < target.scrollWidth;
};

const data = computed(() =>
  state.value.pagingState.type === 'success'
    ? state.value.pagingState.data?.filter((d) => !props.disableUnassignedColumn || d !== UNASSIGNED_COLUMN)
    : []
);
</script>

<template>
  <div class="select-box_container" @click="closeDropdown($event)">
    <div v-if="label !== '' || (isError && errorMessagePosition === 'top')" class="select-box__label">
      <p v-if="label !== ''" class="select-box__label-text">{{ label }}</p>
      <ErrorMessage :is-error="isError" :error-message="errorMessage" />
    </div>
    <div class="select-box">
      <div :class="$slots.selectedListTemplate ? 'select-box__selected--multiple' : 'select-box__selected--single'">
        <div
          :id="`order-status-select-${index}`"
          class="select-box__selected"
          :class="{ 'select-box__selected--disabled': disabled, 'select-box__selected--error': isError }"
          @click="toggleDropdown"
        >
          <div
            v-if="isEllipsis"
            v-tooltip.bottom="selectedRepresent"
            class="select-box__field"
            :class="{ 'select-box__field--placeholder': isPlaceholder, 'select-box__field--disabled': disabled }"
            @mouseover="mouseoverTextLabel"
          >
            <!-- 選択肢した値を表示する。何も選択していない場合はplaceholderの文言を表示する -->
            {{ isPlaceholder ? placeholder : vm.displayValue(filterType, selectedRepresent) }}
          </div>
          <div
            v-else
            class="select-box__field"
            :class="{ 'select-box__field--placeholder': isPlaceholder, 'select-box__field--disabled': disabled }"
            @mouseover="mouseoverTextLabel"
          >
            <!-- 選択肢した値を表示する。何も選択していない場合はplaceholderの文言を表示する -->
            {{ isPlaceholder ? placeholder : vm.displayValue(filterType, selectedRepresent) }}
          </div>
          <SvgLoader v-if="isOpen" name="arrowUp" class="select-box__arrow" />
          <SvgLoader v-else :id="`order-status-select-arrow-${index}`" name="arrowDown" class="select-box__arrow" />
        </div>
        <slot name="selectedListTemplate" :is-open="isOpen" :scroll="true" />
      </div>
      <ErrorMessage
        v-if="props.errorMessagePosition === 'bottom'"
        :style="{ justifySelf: 'start' }"
        :is-error="isError"
        :error-message="errorMessage"
      />
      <template v-if="isOpen">
        <Teleport to="body" :disabled="!useTeleport">
          <div :id="`order-status-select-dropdown-${index}`" class="select-box__dropdown">
            <div v-if="props.useSearchInput" :id="`order-status-select-search-${index}`" class="select-box__search-box-container">
              <input v-model="state.searchText" class="select-box__search-box" type="text" @keydown.enter="onSearchTrigger" />
              <SvgLoader name="searchIcon" class="select-box__search-box-icon" @click="onSearchTrigger" />
            </div>
            <template v-if="state.pagingState.type === 'loading'">
              <div class="select-box__option" />
              <div class="container-first-load">
                <div class="container-first-load__loader">{{ loadingText }}</div>
              </div>
            </template>
            <template v-else-if="state.pagingState.type === 'success'">
              <div v-if="useReset" class="select-box__option" @click="resetOption">
                {{ resetButtonText ?? t('選択を外す') }}
              </div>
              <template v-if="!isFetchRequiredFilterType(filterType) && state.options.length > 0">
                <div
                  v-for="(option, optionIndex) in state.options"
                  :key="option"
                  :class="`select-box__option${$slots.selectBoxOptionCheckboxTemplate ? ' select-box__option--inner' : ''}`"
                  @click="emitOption(option, optionIndex)"
                >
                  <slot name="selectBoxOptionCheckboxTemplate" :option="option" />
                  {{ vm.displayValue(filterType, option) }}
                </div>
              </template>
              <template v-else>
                <div
                  v-for="(option, optionIndex) in data"
                  :key="option"
                  :class="`select-box__option${$slots.selectBoxOptionCheckboxTemplate ? '   select-box__option--inner' : ''}`"
                  @click="emitOption(option, optionIndex)"
                >
                  <slot name="selectBoxOptionCheckboxTemplate" :option="option" />
                  {{ vm.displayValue(filterType, option) }}
                </div>
              </template>
            </template>
            <template v-else-if="state.pagingState.type === 'zeroMatch'">
              <div class="select-box__option" @click="resetOption">
                {{ resetButtonText ?? t('選択を外す') }}
              </div>
              <div class="container-first-load">
                <p class="first-load-error-text">{{ t('要素が１つも存在しません') }}</p>
              </div>
            </template>
            <template v-else-if="state.pagingState.type === 'none' && !isFetchRequiredFilterType(filterType) && state.options.length > 0">
              <template v-if="useReset">
                <div v-if="resetMode === 'reset'" class="select-box__option" @click="resetOption">
                  {{ resetButtonText ?? t('選択を外す') }}
                </div>
                <div v-else class="select-box__option" @click="emitOption('')" />
              </template>

              <div
                v-for="(option, optionIndex) in state.options"
                :key="option"
                class="select-box__option"
                @click="emitOption(option, optionIndex)"
              >
                {{ vm.displayValue(filterType, option) }}
              </div>
            </template>
            <template v-else>
              <div class="container-first-load">
                <p class="first-load-error-text">{{ t('取得に失敗しました') }}</p>
                <div class="select-box__option retry-button" @click.stop="onClickRetryButton()">
                  {{ '再試行' }}
                  <SvgLoader class="calendar-select-button__retry-icon" :name="'retryIcon'" data-testid="retry_icon" />
                </div>
              </div>
            </template>
            <slot
              name="confirmButtonTemplate"
              :click="
                () => {
                  isOpen = false;
                  emit('close');
                }
              "
            />
            <div ref="dropdownSelectedListDom" class="select-box__dropdown--list">
              <slot name="selectedListTemplate" :is-open="isOpen" :scroll="false" />
            </div>
          </div>
        </Teleport>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.select-box {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;

  &__search-box {
    width: 100%;
    border: $semanticPrimary 1px solid;
    background-color: $semanticWhite;
    padding: 6px 32px 6px 8px;
    box-shadow: 0px 0px 16px 0px #4dbde999;
    border-radius: 4px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20.27px;
    text-align: left;

    &-container {
      position: relative;
    }

    &-icon {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0;
  }

  &__label {
    display: grid;
    grid-template-columns: auto auto;
    gap: 3px;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;

    &-text {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 18.2px;
      text-align: center;

      color: $textDefault;
    }
  }

  &__field {
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;
    background-color: $semanticWhite;

    &--placeholder {
      color: $textSecondary;
    }

    &--disabled {
      background-color: $surfaceSecondary;
      color: $textSecondary;
    }
  }

  &__selected {
    width: 100%;
    color: white;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;
    padding: 6px 8px;
    background-color: $semanticWhite;
    border: 1px solid transparent;

    &--single {
      width: 100%;
    }

    &--multiple {
      width: 100%;
      border: 1px solid $borderDefault;
      border-radius: 4px;
      overflow: hidden;
    }

    &--disabled {
      background-color: $surfaceSecondary;
      cursor: not-allowed;
    }

    &--error {
      border: 1px solid $semanticError;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: fixed;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050; // モーダルより高い値に設定
    overflow-y: auto;
    max-height: 300px; // 適切な最大高さを設定
    box-sizing: border-box;

    &--list {
      position: sticky;
      bottom: 0;
    }
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;

    min-height: 32px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--with-loader {
      background-color: $semanticWhite;
    }

    &--disabled {
      color: $surfaceSecondary;
      cursor: not-allowed;
    }

    &--inner {
      display: grid;
      grid-template-columns: min-content auto;
      gap: 4px;
      align-items: center;
    }
  }
}

.container {
  width: 100%; //横幅を画面いっぱいに指定
  height: 48px; //縦幅を・・・同上
  display: flex; //flexBoxでレイアウト制御します。
  justify-content: center; //左右中央に指定
  align-items: center; //上下中央に指定
  flex-direction: column; //ぐるぐると文字を縦並びにする指定
  background-color: $semanticWhite !important;
  &:hover {
    background-color: $semanticWhite !important;
  }
}

.container-first-load {
  width: 100%; //横幅を画面いっぱいに指定
  height: 280px; //縦幅を・・・同上
  display: flex !important; //flexBoxでレイアウト制御します。
  justify-content: center !important; //左右中央に指定
  align-items: center !important; //上下中央に指定
  flex-direction: column; //ぐるぐると文字を縦並びにする指定
  background-color: $semanticWhite !important;
  &:hover {
    background-color: $semanticWhite !important;
  }

  &__loader {
    font-family: Noto Sans JP;
    color: $semanticPrimary;
    font-weight: 700;
    font-size: $fontSizeS;
    height: 30px;
  }
}
.loader {
  font-family: Noto Sans JP;
  color: $semanticPrimary;
  font-weight: 700;
  font-size: $fontSizeS;
  padding-bottom: 4px;
  &:hover {
    background-color: $semanticWhite;
  }
}
.see-more-button {
  color: $semanticPrimary;
  font-weight: 700;
  font-size: $fontSizeS;
  display: flex !important;
  justify-content: center !important;
  gap: 4px;
  @include hover_black;
  width: 100%;

  &:hover {
    background-color: $semanticWhite;
  }
}

.retry-button {
  color: $semanticPrimary;
  font-weight: 700;
  font-size: $fontSizeS;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 4px;
  @include hover_black;
  width: 100%;

  &:hover {
    background-color: $semanticWhite;
  }
}

.first-load-error-text {
  font-size: 12px;
  color: $textSecondary;
}
</style>
